import { useQuery } from '@tanstack/react-query';
import remoteDbService from '@/core/database/RemoteDBService';
import { type Collection } from '@/features/collections/types/collection.types';
import { type Page } from '@/features/pages/types/page.types';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';

type CollectionWithFirstPageData = {
  collection: Collection;
  firstPage: Page | null;
};

export const useCollectionWithFirstPage = (collectionId: string | undefined, pageId?: string) => {
  const currentCollectionId = useCollectionStore(state => state.currentCollection?.id);

  // Primary query: Get collection and specific page OR first page
  return useQuery<CollectionWithFirstPageData>({
    queryKey: [`collection-first-page/${collectionId || ''}`, { pageId }],
    queryFn: async () => {
      if (!collectionId) throw new Error('Collection ID is required');

      if (pageId) {
        // Load collection and specific page
        const collection = await remoteDbService.getPublicCollectionById(collectionId);
        return {
          collection,
          firstPage: null, // the note/page query will be loaded from the PublicCollectionNote component
        };
      } else {
        // Load collection and first page
        return await remoteDbService.getCollectionByIdAndFirstPage(collectionId);
      }
    },
    enabled: !!collectionId && collectionId !== currentCollectionId,
  });
};
